/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { ShiftCrossLocationOpen } from "@microsoft/shifts-models-data";
import { OpenShiftInfo } from "@microsoft/shifts-models-view";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { DataService } from "sh-services/dataservice/DataService";

import { CrossLocationOpenShiftDataProvider } from "./CrossLocationOpenShiftDataProvider";
import { ICrossLocationOpenShiftDataService } from "./ICrossLocationOpenShiftDataService";

/**
 * The data service for cross-location open shifts.
 */
export class CrossLocationOpenShiftDataService extends DataService implements ICrossLocationOpenShiftDataService {
    /**
     * Gets the cross-location open shifts for the current user and given team id.
     * @param teamId The team id.
     * @param crossLocationTeamId The cross-location team id.
     * @param setDataInState The callback to set the view models data in state.
     * @returns The cross-location open shifts.
     */
    public async getCrossLocationOpenShifts(
        teamId: string,
        crossLocationTeamId: string,
        setDataInState: (openShifts: OpenShiftInfo[]) => void
    ): Promise<OpenShiftInfo[]> {
        const setDataInMemory = (openShifts: ShiftCrossLocationOpen[]): void => {
            // Note: since ShiftCrossLocationOpen data model is same as OpenShiftInfo view model, we don't have to use a mapper to convert the data models to view models.
            setDataInState(openShifts);
        };

        const provider = new CrossLocationOpenShiftDataProvider({
            crossLocationTeamId,
            database: this.crossLocationOpenShiftDatabase,
            setDataInMemory,
            teamId,
            tenantId: this.tenantId,
            userId: this.loggedInUserId
        });

        const openShifts = await this.getData<ShiftCrossLocationOpen[]>(provider);

        return openShifts;
    }

    /**
     * Resets the sync state.
     */
    public async resetSyncState(): Promise<void> {
        return Promise.resolve();
    }
}
